// Landing-page → tool-intent helpers for the signup hand-off.
//
// The dashboard deep-links new users into the tool they signed up for via a
// `preset` slug (stored in localStorage by /login?preset=... and consumed in
// src/pages/dashboard.tsx). These helpers derive that slug from a marketing
// landing-page path so signup CTAs and the dashboard's intent fallback agree
// on one mapping.

// Locale prefixes from i18n.json — landing paths may carry one.
const LOCALE_PREFIX = /^\/(?:ja|es|fr|it|de|ko|zh|th|ar|pt|ms|vi)(?=\/|$)/;

const PATH_PRESETS: Array<[RegExp, string]> = [
  [/^\/(?:ai-video-upscaler|upscale-video)(?:\/|$)/, 'upscale-video'],
  [/^\/(?:ai-video-extender|extend-video)(?:\/|$)/, 'extend-video'],
  [/^\/(?:edit-video-with-ai|edit-video)(?:\/|$)/, 'edit-video'],
  // Talking-video landing pages promote the talking-avatar product, not the
  // general video generator — must match before the ai-video-generator rule.
  [
    /^\/(?:ai-talking-videos|ai-video-generator\/(?:talking|talking-avatar|talking-head|spokesperson|make-image-talk))(?:\/|$)/,
    'ai-talking-videos',
  ],
  [/^\/(?:ai-video-generator|ai-animate-image|ai-videos)(?:\/|$)/, 'ai-videos'],
  [/^\/create-video(?:\/|$)/, 'create-video'],
  [/^\/text-to-speech(?:\/|$)/, 'tts'],
  // AI Photo Studio (trained models) — must match before the generic image rule.
  [/^\/ai-photo-generator(?:\/|$)/, 'photostudio'],
  [/^\/(?:ai-image-generator|ai-images)(?:\/|$)/, 'ai-images'],
  [
    /^\/(?:ai-interior-design-generator|ai-interior-designer|ai-virtual-staging|paint-color-visualizer|ai-landscape-design|ai-exterior-design|interior-design-styles)(?:\/|$)/,
    'ai-interior-designer',
  ],
  [
    /^\/(?:music-generator|ai-music-generator|ai-song-generator)(?:\/|$)/,
    'music',
  ],
  [/^\/(?:audio-generator|ai-audio-generator)(?:\/|$)/, 'audio-generator'],
  [/^\/(?:sound-effects|ai-sound-effects-generator)(?:\/|$)/, 'sound'],
  [/^\/ai-transcription(?:\/|$)/, 'transcription'],
  [/^\/(?:note-taker|ai-note-taker)(?:\/|$)/, 'note-taker'],
  [/^\/voices(?:\/|$)/, 'voices'],
];

// Templates whose signup hand-off goes to a different tool than
// /presets/<slug> — mirrors the `loginPreset` fields in
// src/constants/presets.ts (add new `loginPreset` entries here too).
const TEMPLATE_LOGIN_PRESETS: Record<string, string> = {
  'company-profile-generator': 'chat?mode=create-slides',
  'podcast-introduction-generator': 'chat',
  'classroom-jeopardy-generator': 'chat',
  'html-css-code-generator': 'chat',
  'python-code-generator': 'chat',
  'java-code-generator': 'chat',
};

export function presetFromPath(path: string | null | undefined): string | null {
  if (!path) return null;

  let cleaned = path.split(/[?#]/)[0].replace(LOCALE_PREFIX, '');
  if (!cleaned.startsWith('/')) cleaned = `/${cleaned}`;

  // Template landing pages map to their preset slug (tool lives at
  // /presets/<slug>), unless the template configures a different destination
  // via loginPreset.
  const template = cleaned.match(/^\/templates\/([a-z0-9-]+)/);
  if (template) return TEMPLATE_LOGIN_PRESETS[template[1]] || template[1];

  for (const [pattern, preset] of PATH_PRESETS) {
    if (pattern.test(cleaned)) return preset;
  }

  return null;
}

// Signup href for landing-page CTAs: carries tool intent when the current
// path implies one, and always opens the sign-up form (not log-in).
export function signupHrefForPath(path: string | null | undefined): string {
  const preset = presetFromPath(path);
  return preset
    ? `/login?preset=${encodeURIComponent(preset)}#auth-sign-up`
    : '/login#auth-sign-up';
}

// Log-in href for landing-page nav links: returning users also get
// deep-linked back into the tool the page promotes instead of the dashboard.
export function loginHrefForPath(path: string | null | undefined): string {
  const preset = presetFromPath(path);
  return preset ? `/login?preset=${encodeURIComponent(preset)}` : '/login';
}

// The dashboard's preset → tool URL mapping. Unknown presets are treated as
// template slugs, matching the historical behavior.
export function urlForPreset(preset: string, lang: string): string {
  const localize = (path: string) => (lang === 'en' ? path : `/${lang}${path}`);

  if (preset === 'chat' || preset.startsWith('chat?')) {
    const queryIdx = preset.indexOf('?');
    return localize('/chat') + (queryIdx >= 0 ? preset.slice(queryIdx) : '');
  }

  const direct: Record<string, string> = {
    'ai-images': '/ai-images',
    photostudio: '/ai-images/models',
    'ai-interior-designer': '/ai-interior-designer',
    'note-taker': '/note-taker',
    voices: '/voices',
    'ai-videos': '/ai-videos',
    'upscale-video': '/ai-videos?tab=upscale-video',
    'extend-video': '/ai-videos?tab=extend-video',
    'edit-video': '/ai-videos?tab=edit-video',
    'create-video': '/create-video',
    'ai-talking-videos': '/ai-talking-videos',
    tts: '/tts',
    templates: '/presets',
    transcription: '/audios',
    audios: '/audios',
    music: '/music-generator',
    sound: '/sound-effects',
    'audio-generator': '/audio-generator',
    bots: '/agents',
  };

  return localize(direct[preset] || `/presets/${preset}`);
}
